import { useReducer, useContext, useEffect, useRef } from 'react';
import { TabsContext } from './TabsContext.js';
import { compareDOMOrder } from './util.js';

const isTabListItem = (payload) => typeof payload === 'object' &&
    payload !== null &&
    'ref' in payload &&
    typeof payload.props !== 'undefined' &&
    typeof payload.props.id === 'string' &&
    payload.props.id.length > 0;
function useTabsState() {
    function reducer(state, action) {
        var _a;
        if (action.type === 'select') {
            return {
                ...state,
                selected: action.payload.ref,
            };
        }
        if (action.type === 'register') {
            if (isTabListItem(action.payload)) {
                return {
                    ...state,
                    items: state.items
                        .map(({ ref }) => ref)
                        .includes(action.payload.ref)
                        ? state.items
                        : [...state.items, action.payload].sort((a, b) => compareDOMOrder(a.ref, b.ref)),
                    selected: action.payload.props.selected
                        ? // Select tab, is selected prop is set
                            action.payload.ref
                        : // Select tab, if no tab is selected
                            (_a = state.selected) !== null && _a !== void 0 ? _a : action.payload.ref,
                };
            }
            else {
                throw new Error('Tabs: Unable to register a Tab without a valid id!');
            }
        }
        if (action.type === 'unregister') {
            return {
                ...state,
                items: state.items.filter(({ ref }) => ref !== action.payload.ref),
            };
        }
        return state;
    }
    return useReducer(reducer, {
        selected: undefined,
        items: [],
    });
}
function useTabList(props, ref) {
    const tabsContext = useContext(TabsContext);
    const { dispatch } = tabsContext !== null && tabsContext !== void 0 ? tabsContext : {};
    useEffect(() => {
        if (dispatch && ref && 'current' in ref) {
            dispatch({ type: 'register', payload: { props, ref } });
        }
        return () => {
            if (dispatch && ref && 'current' in ref) {
                dispatch({ type: 'unregister', payload: { ref } });
            }
        };
    }, [dispatch, ref]);
    return tabsContext;
}
/**
 * An utility hook for maintaining a dynamic list of references
 *
 * @param initialValue Initial reference map or `null`. Defaults to `null`.
 * @returns `[getRefMap, refCallback]` - A tuple of two functions:
 * - `getRefMap()` returns the current reference map
 * - `refCallback(key)` returns a callback ref that can be used in a `ref` prop.
 * @see https://react.dev/learn/manipulating-the-dom-with-refs#how-to-manage-a-list-of-refs-using-a-ref-callback
 */
function useRefMap(initialValue = null) {
    const refs = useRef(initialValue);
    const getRefMap = () => {
        if (!refs.current) {
            refs.current = new Map();
        }
        return refs.current;
    };
    const refCallback = (key) => (value) => {
        const map = getRefMap();
        if (value) {
            map.set(key, value);
        }
        else {
            map.delete(key);
        }
    };
    return [getRefMap, refCallback];
}

export { useRefMap, useTabList, useTabsState };
//# sourceMappingURL=hooks.js.map
